import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom'
import { Badge, Alert, Card, Row, Col } from 'react-bootstrap'
import { JsonViewer } from '../../../../../../../common/JsonViewer.jsx';
import { HeaderTitle } from '../../../../../../../common/HeaderTitle.jsx';
import InputNumericWiz from '../../../../../../../common/inputs/wizard/InputNumericWiz.jsx'
import { toCurrency } from '../../../../../../../utils/helpers/index.js'
import { useProjektData } from '../../../../../../../../api/domain/elementyRejstru/queries/useProjektData.jsx'

const ZRODLA_LIST_KEY = 'finansowanie_list'

const ZrodloItemCard = ({ row, idx, setRow, wiz }) => {
    const plan = parseFloat(row?.kwota || 0)
    const wydatkowanie = parseFloat(row?.wydatkowanie_narastajaco || 0)
    const percent = plan > 0 ? (wydatkowanie / plan) * 100 : 0
    const percentValue = Number.isFinite(percent) ? percent.toFixed(2) : ''
    const percentVariant = percent > 100 ? 'danger' : 'success'

    return (
        <Card className="mb-3 shadow-sm border-primary" style={{ borderLeftWidth: '6px' }}>
            <Card.Header className="bg-light py-3">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <span className="fw-bold fs-5">
                        <Badge bg="secondary" className="me-2 p-2">#{idx + 1}</Badge>
                        {row?.nazwa_zrodla || 'Źródło finansowania'}
                    </span>
                </div>
            </Card.Header>

            <Card.Body className="py-3">
                <Row className="align-items-stretch">
                    <Col lg={4} xs={12} className="mb-3 mb-md-0 border-end px-3 d-flex flex-column justify-content-center">
                        <div className="small">
                            <span>
                                Budżet planowany [ZŁ]:{' '}
                                <strong>{toCurrency(plan)}</strong>
                            </span>
                        </div>
                    </Col>
                    <Col lg={4} xs={12} className="mb-3 mb-md-0 border-end px-3 d-flex flex-column justify-content-center">
                        <InputNumericWiz
                            row={row}
                            setRow={setRow}
                            idx={idx}
                            wiz={wiz}
                            keyName="wydatkowanie_narastajaco"
                            label="Wydatkowanie ogółem [ZŁ]"
                            decimal={2}
                            max={99999999999999.99}
                            className="text-end"
                        />
                    </Col>
                    <Col lg={4} xs={12} className="mb-3 mb-md-0 border-end px-3 d-flex flex-column justify-content-center">
                        <small>% Wydatkowania ogółem</small>
                        <div className={`fw-bold fs-6 text-${percentVariant}`}>
                            {Number.isFinite(percent)
                                ? percentValue + '%'
                                : '-'}
                        </div>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    )
}

ZrodloItemCard.propTypes = {
    row: PropTypes.object.isRequired,
    idx: PropTypes.number.isRequired,
    setRow: PropTypes.func.isRequired,
    wiz: PropTypes.any,
}

const FinansowanieSummaryCard = ({ summary }) => {
    const percentVariant = summary.percent > 100 ? 'danger' : 'primary'
    
    return (
        <Card className="mb-4 mt-5 p-3 border-primary">
            <Card.Body>
                <h4 className="text-primary fw-bold mb-4 border-bottom pb-2">
                    Podsumowanie Finansowania
                </h4>
                <Row className="text-center">
                    <Col md={3} className="mb-3 mb-md-0 border-end">
                        <div className="text-muted small text-uppercase">Okres realizacji</div>
                        <div className="fw-bold mt-1 text-dark">
                            <i className="bi bi-calendar-range me-2"></i>
                            <Badge bg={'primary'} className="fs-5 p-2">
                                {summary.range}
                            </Badge>
                        </div>
                    </Col>

                    <Col md={3} className="mb-3 mb-md-0 border-end">
                        <div className="text-muted small text-uppercase">
                            Budżet planowany [ZŁ]
                        </div>
                        <div className="fw-bold mt-1">
                            <Badge bg="primary" className="fs-5 p-2">
                                {toCurrency(summary.totalPlan)}
                            </Badge>
                        </div>
                    </Col>

                    <Col md={3} className="mb-3 mb-md-0 border-end">
                        <div className="text-muted small text-uppercase">
                            Suma wydatkowania ogółem [ZŁ]
                        </div>
                        <div className="fw-bold mt-1">
                            <Badge bg="primary" className="fs-5 p-2">
                                {toCurrency(summary.totalSpent)}
                            </Badge>
                        </div>
                    </Col>

                    <Col md={3}>
                        <div className="text-muted small text-uppercase">
                            % Wydatkowania ogółem
                        </div>
                        <div className="fw-bold mt-1">
                            <Badge bg={percentVariant} className="fs-5 p-2">
                                {summary.percent.toFixed(2)}%
                            </Badge>
                        </div>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    )
}

FinansowanieSummaryCard.propTypes = {
    summary: PropTypes.shape({
        totalPlan: PropTypes.number.isRequired,
        totalSpent: PropTypes.number.isRequired,
        percent: PropTypes.number.isRequired,
        range: PropTypes.string.isRequired,
    }).isRequired,
}

const FinansowanieForm = ({ wiz }) => {
    const list = useMemo(() => (
        Array.isArray(wiz?.form?.[ZRODLA_LIST_KEY]) ? wiz.form[ZRODLA_LIST_KEY] : []
    ), [wiz?.form?.[ZRODLA_LIST_KEY]])

    const dataRozpoczecia = wiz?.form?.data_rozpoczecia
    const dataZakonczenia = wiz?.form?.data_zakonczenia

    const formatDate = (dateStr) => {
        if (!dateStr) return '-'
        const date = new Date(dateStr)
        if (isNaN(date.getTime())) return dateStr
        return date.toLocaleDateString('pl-PL')
    }

    const summary = useMemo(() => {
        const totalPlan = list.reduce((s, r) => s + parseFloat(r?.kwota || 0), 0)
        const totalSpent = list.reduce((s, r) => s + parseFloat(r?.wydatkowanie_narastajaco || 0), 0)
        const percent = totalPlan > 0 ? (totalSpent / totalPlan) * 100 : 0
        const range = `${formatDate(dataRozpoczecia)} - ${formatDate(dataZakonczenia)}`
        return { totalPlan, totalSpent, percent: Number.isFinite(percent) ? percent : 0, range }
    }, [list, dataRozpoczecia, dataZakonczenia])

    const updateItem = (index, newRow) => {
        const next = [...list]
        next[index] = newRow
        if (typeof wiz?.setValue === 'function') {
            wiz.setValue(ZRODLA_LIST_KEY, next)
        } else if (typeof wiz?.setWizForm === 'function') {
            wiz.setWizForm({ ...(wiz?.form || {}), [ZRODLA_LIST_KEY]: next })
        }
    }

    if (list.length === 0) {
        return <Alert variant="info" className="mt-3">Brak źródeł finansowania z karty projektu.</Alert>
    }

    return (
        <div className="d-flex flex-column gap-2">
            {list.map((row, idx) => {
                const setRow = (key, value) => {
                    if (row?.[key] === value) return
                    const newRow = { ...row, [key]: value }
                    if (key === 'wydatkowanie_narastajaco') {
                        const plan = parseFloat(newRow?.kwota || 0)
                        const wyd = parseFloat(value || 0)
                        const percent = plan > 0 ? (wyd / plan) * 100 : 0
                        newRow.procent_wydatkowania = Number.isFinite(percent) ? percent.toFixed(2) : ''
                    }
                    updateItem(idx, newRow)
                }
                return <ZrodloItemCard key={idx} row={row} idx={idx} setRow={setRow} wiz={wiz} />
            })}

            <FinansowanieSummaryCard summary={summary} />
        </div>
    )
}

FinansowanieForm.propTypes = { wiz: PropTypes.any }

const Finansowanie = (props) => {
    const { rejestrId } = useParams()
    const prefilledRef = useRef(false)

    const { data: projektData } = useProjektData(rejestrId, !!rejestrId)

    useEffect(() => {
        if (prefilledRef.current) return
        if (!props.wiz) return

        const current = props.wiz.form || {}
        const currentList = current[ZRODLA_LIST_KEY]
        const hasCurrent = Array.isArray(currentList) && currentList.length > 0
        if (!hasCurrent && !projektData) return

        const normalize = (z) => {
            const plan = parseFloat(z?.kwota || 0)
            const wyd = parseFloat(z?.wydatkowanie_narastajaco || 0)
            const percent = plan > 0 ? (wyd / plan) * 100 : 0
            const computedPercent = Number.isFinite(percent) ? percent.toFixed(2) : ''
            const hasPercent = ![null, undefined, ''].includes(z?.procent_wydatkowania)
            return {
                nazwa_zrodla: z?.nazwa_zrodla ?? '',
                typ_zrodla: z?.typ_zrodla ?? '',
                kwota: z?.kwota ?? '0',
                wydatkowanie_narastajaco: z?.wydatkowanie_narastajaco ?? '0',
                procent_wydatkowania: hasPercent ? z.procent_wydatkowania : computedPercent,
            }
        }

        const source = hasCurrent
            ? currentList
            : (Array.isArray(projektData?.zrodla_list) ? projektData.zrodla_list : [])

        const nextList = source.map(normalize)
        const hasChanges = JSON.stringify(nextList) !== JSON.stringify(currentList || [])

        // Pobierz daty z karty projektu jeśli nie są jeszcze w formularzu
        const dataRozpoczecia = current.data_rozpoczecia || projektData?.data_rozpoczecia || ''
        const dataZakonczenia = current.data_zakonczenia || projektData?.data_zakonczenia || ''

        if (hasChanges || (!current.data_rozpoczecia && dataRozpoczecia) || (!current.data_zakonczenia && dataZakonczenia)) {
            props.wiz.setWizForm({ 
                ...current, 
                [ZRODLA_LIST_KEY]: nextList,
                data_rozpoczecia: dataRozpoczecia,
                data_zakonczenia: dataZakonczenia,
            })
        }
        prefilledRef.current = true
    }, [props.wiz, projektData])

    return (
        <>
            <JsonViewer src={props.wiz?.form || {}} show={false} />
            <HeaderTitle noHr size={'fs-3'} title={'B. Źródła finansowania'} />
            <HeaderTitle
                noHr
                size={'fs-4'}
                title={'Monitorowanie wykonania budżetu projektu od początku jego realizacji (wydatkowanie narastająco)'}
            />
            <FinansowanieForm wiz={props.wiz} />
        </>
    )
}

export default Finansowanie
